import { inject, Injectable } from "@angular/core";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import {
  EMPTY,
  Observable,
  Subject,
  finalize,
  iif,
  map,
  takeUntil,
  tap,
} from "rxjs";
import {
  OrganizationsDatabaseService,
  PublishedOpportunityService,
  SchoolService,
  StudentService,
} from "@platform-app/app/core/api/services";
import {
  Pagination,
  SearchOrganizationsRequest,
  SearchPublishedOpportunitiesRequest,
  SmartMatchClinicsToSchoolRequest,
  SmartMatchClinicsToStudentRequest,
  SmartMatchOpportunitiesRequest,
} from "@platform-app/app/core/api/models";
import { DEFAULT_PLACEMENTS_DATABASE_STATE } from "@platform-app/app/main/placements-database/shared/store/constants";
import { PlacementsDatabaseStateModel } from "@platform-app/app/main/placements-database/shared/store/models";
import {
  ClearPlacementsDatabaseState,
  LoadPlacementsDatabaseStatistics,
  MarkerSelected,
  PreviewOpportunity,
  OrganizationDetailsTabChanged,
  PreviewOrganization,
  SetCurrentViewport,
  SetPlacementsDatabaseListType,
  TogglePlacementListVisibilityState,
  UpdatePlacementsDatabaseFilters,
  UpdatePlacementsDatabasePagination,
  LoadPlacementsDatabaseList,
  SetOpportunityDetailsId,
  SetSmartMatchStudent,
  SetPlacementsDatabaseFiltersTab,
  LoadSmartMatchStudents,
  CloseStudentNotificationTooltip,
  InitializePlacementsDatabaseFilters,
  UpdatePlacementsDatabaseLocationFilters,
  LoadSmartMatchSchools,
  SetSmartMatchSchool,
  CloseSchoolNotificationTooltip,
} from "@platform-app/app/main/placements-database/shared/store/actions";
import {
  Organization,
  PlacementsDatabaseFilterTab,
  PlacementsDatabaseListType,
} from "@platform-app/app/main/placements-database/shared/models";
import { DateUtility } from "@platform-app/app/shared/common/utilities";
import { StateToDefaultResetter } from "@platform-app/app/shared/common/store/state-to-default-resetter/state-to-default-resetter";
import {
  SignInAsUser,
  SignInToOriginalAccount,
  SignInWithGoogle,
  SignInWithPassword,
} from "@platform-app/app/core/auth/auth.actions";

@State<PlacementsDatabaseStateModel>({
  name: "PlacementsDatabase",
  defaults: DEFAULT_PLACEMENTS_DATABASE_STATE,
})
@Injectable()
export class PlacementsDatabaseState extends StateToDefaultResetter<PlacementsDatabaseStateModel> {
  private readonly organizationsDatabaseService = inject(
    OrganizationsDatabaseService,
  );
  private readonly opportunitiesService = inject(PublishedOpportunityService);
  private readonly studentService = inject(StudentService);
  private readonly schoolService = inject(SchoolService);

  private readonly cancelPrevious$ = new Subject<void>();

  constructor() {
    super(DEFAULT_PLACEMENTS_DATABASE_STATE);
  }

  @Selector()
  static isLoading(state: PlacementsDatabaseStateModel) {
    return state.isLoading;
  }

  @Selector()
  static organizations(state: PlacementsDatabaseStateModel) {
    return state.organizations;
  }

  @Selector()
  static previewOrganizationId(state: PlacementsDatabaseStateModel) {
    return state.previewOrganizationId;
  }

  @Selector()
  static manualFilters(state: PlacementsDatabaseStateModel) {
    return state.manualFilters;
  }

  @Selector()
  static locationFilters(state: PlacementsDatabaseStateModel) {
    return state.locationFilters;
  }

  @Selector()
  static pagination(state: PlacementsDatabaseStateModel) {
    return state.pagination;
  }

  @Selector()
  static isListVisible(state: PlacementsDatabaseStateModel) {
    return state.isListVisible;
  }

  @Selector()
  static selectedTab(state: PlacementsDatabaseStateModel) {
    return state.selectedTab;
  }

  @Selector()
  static listType(state: PlacementsDatabaseStateModel) {
    return state.listType;
  }

  @Selector()
  static databaseStatistics(state: PlacementsDatabaseStateModel) {
    return state.databaseStatistics;
  }

  @Selector()
  static opportunities(state: PlacementsDatabaseStateModel) {
    return state.opportunities;
  }

  @Selector()
  static itemsCount(state: PlacementsDatabaseStateModel) {
    return state.itemsCount;
  }

  @Selector()
  static previewOpportunityId(state: PlacementsDatabaseStateModel) {
    return state.previewOpportunityId;
  }

  @Selector()
  static opportunityDetailsId(state: PlacementsDatabaseStateModel) {
    return state.opportunityDetailsId;
  }

  @Selector()
  static smartMatchStudents(state: PlacementsDatabaseStateModel) {
    return state.smartMatchStudents;
  }

  @Selector()
  static smartMatchStudent(state: PlacementsDatabaseStateModel) {
    return (
      state.smartMatchStudents?.find(
        (student) => student.id === state.smartMatchStudentId,
      ) || null
    );
  }

  @Selector()
  static smartMatchStudentId(state: PlacementsDatabaseStateModel) {
    return state.smartMatchStudentId;
  }

  @Selector()
  static smartMatchMessageDismissedForStudents(
    state: PlacementsDatabaseStateModel,
  ) {
    return state.smartMatchMessageDismissedForStudents;
  }

  @Selector()
  static smartMatchMessageDismissedForSchools(
    state: PlacementsDatabaseStateModel,
  ) {
    return state.smartMatchMessageDismissedForSchools;
  }

  @Selector()
  static smartMatchSchools(state: PlacementsDatabaseStateModel) {
    return state.smartMatchSchools;
  }

  @Selector()
  static smartMatchSchool(state: PlacementsDatabaseStateModel) {
    return (
      state.smartMatchSchools?.find(
        (school) => school.id === state.smartMatchSchoolId,
      ) || null
    );
  }

  @Selector()
  static smartMatchSchoolId(state: PlacementsDatabaseStateModel) {
    return state.smartMatchSchoolId;
  }

  @Selector()
  static filtersTab(state: PlacementsDatabaseStateModel) {
    return state.filtersTab;
  }

  @Selector()
  static filtersManuallyModified(state: PlacementsDatabaseStateModel) {
    return state.filtersManuallyModified;
  }

  @Selector()
  static filtersInitialized(state: PlacementsDatabaseStateModel) {
    return state.filtersInitialized;
  }

  @Action(LoadPlacementsDatabaseList)
  loadList(
    ctx: StateContext<PlacementsDatabaseStateModel>,
    action: LoadPlacementsDatabaseList,
  ) {
    const { pagination } = ctx.getState();
    const { resetPage = false } = action.payload;

    if (resetPage) {
      ctx.patchState({
        pagination: { ...pagination, currentPage: 1 },
      });
    }

    return this.loadListItems(ctx);
  }

  @Action(UpdatePlacementsDatabasePagination)
  updatePlacementsDatabasePagination(
    ctx: StateContext<PlacementsDatabaseStateModel>,
    action: UpdatePlacementsDatabasePagination,
  ) {
    const { pagination } = action.payload;

    ctx.patchState({
      pagination,
    });

    return this.loadListItems(ctx);
  }

  @Action(PreviewOrganization)
  previewOrganization(
    ctx: StateContext<PlacementsDatabaseStateModel>,
    action: PreviewOrganization,
  ) {
    const stateSnapshot = ctx.getState();
    const { organizationId } = action.payload;

    if (organizationId === stateSnapshot.previewOrganizationId) return;

    ctx.patchState({
      previewOrganizationId: organizationId,
    });
  }

  @Action(PreviewOpportunity)
  previewOpportunity(
    ctx: StateContext<PlacementsDatabaseStateModel>,
    action: PreviewOpportunity,
  ) {
    const { opportunityId } = action.payload;

    ctx.patchState({
      previewOpportunityId: opportunityId,
    });
  }

  @Action(SetOpportunityDetailsId)
  setOpportunityDetailsId(
    ctx: StateContext<PlacementsDatabaseStateModel>,
    action: SetOpportunityDetailsId,
  ) {
    const { id } = action.payload;

    ctx.patchState({
      opportunityDetailsId: id,
    });
  }

  @Action(MarkerSelected)
  markerSelected(
    ctx: StateContext<PlacementsDatabaseStateModel>,
    action: MarkerSelected,
  ) {
    const { listType } = ctx.getState();
    const { markerId } = action.payload;

    return ctx.dispatch(
      listType === PlacementsDatabaseListType.Opportunities
        ? new PreviewOpportunity({ opportunityId: markerId })
        : new PreviewOrganization({ organizationId: markerId }),
    );
  }

  @Action(OrganizationDetailsTabChanged)
  organizationDetailsTabChanged(
    ctx: StateContext<PlacementsDatabaseStateModel>,
    action: OrganizationDetailsTabChanged,
  ) {
    ctx.patchState({
      selectedTab: action.payload.selectedTab,
    });
  }

  @Action(UpdatePlacementsDatabaseLocationFilters)
  updateLocationFilters(
    ctx: StateContext<PlacementsDatabaseStateModel>,
    action: UpdatePlacementsDatabaseLocationFilters,
  ) {
    const { locationFilters, pagination } = ctx.getState();
    const {
      placeId = locationFilters?.placeId || null,
      location = locationFilters?.location || null,
      applyViewPort = true,
    } = action.payload;

    ctx.patchState({
      pagination: { ...pagination, currentPage: 1 },
      locationFilters: location
        ? {
            placeId,
            location,
          }
        : null,
    });

    return this.loadListItems(ctx, applyViewPort);
  }

  @Action(UpdatePlacementsDatabaseFilters)
  updateManualFilters(
    ctx: StateContext<PlacementsDatabaseStateModel>,
    action: UpdatePlacementsDatabaseFilters,
  ) {
    const stateSnapshot = ctx.getState();
    const { manualFilters, pagination } = stateSnapshot;
    const {
      searchText = manualFilters.searchText,
      clinicTypesIds = manualFilters.clinicTypesIds,
      schoolTypesIds = manualFilters.schoolTypesIds,
      disciplinesIds = manualFilters.disciplinesIds,
      specialtiesIds = manualFilters.specialtiesIds,
      opportunityTypes = manualFilters.opportunityTypes,
      startDate = manualFilters.startDate,
      endDate = manualFilters.endDate,
      applyViewPort = true,
    } = action.payload;

    ctx.patchState({
      pagination: { ...pagination, currentPage: 1 },
      manualFilters: {
        searchText,
        clinicTypesIds,
        schoolTypesIds,
        disciplinesIds,
        specialtiesIds,
        opportunityTypes,
        startDate,
        endDate,
      },
      filtersManuallyModified: true,
    });

    return this.loadListItems(ctx, applyViewPort);
  }

  @Action(SetCurrentViewport)
  setCurrentViewport(
    ctx: StateContext<PlacementsDatabaseStateModel>,
    action: SetCurrentViewport,
  ) {
    const { viewport: stateViewPort } = ctx.getState();
    const { viewport = stateViewPort } = action.payload;

    ctx.patchState({
      viewport,
    });
  }

  @Action(ClearPlacementsDatabaseState)
  clearPartnersState(ctx: StateContext<PlacementsDatabaseStateModel>) {
    ctx.patchState({
      pagination: {
        currentPage: 1,
        itemsPerPage: 50,
      },
      isLoading: true,
      opportunities: null,
      organizations: null,
      itemsCount: 0,
      previewOpportunityId: null,
      opportunityDetailsId: null,
      previewOrganizationId: null,
      smartMatchStudents: null,
      smartMatchSchools: null,
      databaseStatistics: null,
      selectedTab: null,
      viewport: null,
      manualFilters: { ...ctx.getState().manualFilters, searchText: null },
    });
  }

  @Action(SignInToOriginalAccount)
  @Action(SignInAsUser)
  @Action(SignInWithGoogle)
  @Action(SignInWithPassword)
  resetList(ctx: StateContext<PlacementsDatabaseStateModel>): void {
    ctx.setState(DEFAULT_PLACEMENTS_DATABASE_STATE);
  }

  @Action(TogglePlacementListVisibilityState)
  toggleListVisibilityState(ctx: StateContext<PlacementsDatabaseStateModel>) {
    const state = ctx.getState();

    ctx.patchState({
      isListVisible: !state.isListVisible,
    });
  }

  @Action(LoadPlacementsDatabaseStatistics)
  loadPlacementsDatabaseStatistics(
    ctx: StateContext<PlacementsDatabaseStateModel>,
  ) {
    ctx.patchState({ isLoading: true });

    return this.organizationsDatabaseService
      .getOrganizationsDatabaseStatistics()
      .pipe(
        tap((response) =>
          ctx.patchState({
            databaseStatistics: response,
            listType:
              ctx.getState().listType ??
              (response.opportunitiesCount
                ? PlacementsDatabaseListType.Opportunities
                : PlacementsDatabaseListType.Organizations),
          }),
        ),
        finalize(() => ctx.patchState({ isLoading: this.isListLoading(ctx) })),
      );
  }

  @Action(SetPlacementsDatabaseListType)
  setPlacementsDatabaseListType(
    ctx: StateContext<PlacementsDatabaseStateModel>,
    action: SetPlacementsDatabaseListType,
  ) {
    const { pagination } = ctx.getState();
    const { type, refresh = true } = action.payload;

    ctx.patchState({
      listType: type,
      pagination: { ...pagination, currentPage: 1 },
    });

    return refresh ? this.loadListItems(ctx) : EMPTY;
  }

  @Action(SetSmartMatchStudent)
  setSmartMatchStudent(
    ctx: StateContext<PlacementsDatabaseStateModel>,
    action: SetSmartMatchStudent,
  ) {
    const { smartMatchStudentId } = ctx.getState();
    const { studentId } = action.payload;
    if (smartMatchStudentId === studentId) return;

    ctx.patchState({
      smartMatchStudentId: studentId,
      filtersTab: PlacementsDatabaseFilterTab.StudentSmartMatch,
      isLoading: true,
    });
  }

  @Action(SetSmartMatchSchool)
  setSmartMatchSchool(
    ctx: StateContext<PlacementsDatabaseStateModel>,
    action: SetSmartMatchSchool,
  ) {
    const { smartMatchSchoolId } = ctx.getState();
    const { schoolId } = action.payload;
    if (smartMatchSchoolId === schoolId) return;

    ctx.patchState({
      smartMatchSchoolId: schoolId,
      filtersTab: PlacementsDatabaseFilterTab.SchoolSmartMatch,
      isLoading: true,
    });
  }

  @Action(LoadSmartMatchStudents)
  loadSmartMatchStudents(ctx: StateContext<PlacementsDatabaseStateModel>) {
    ctx.patchState({ isLoading: true });

    return this.studentService
      .studentAvailableForMatchingPost({
        body: { disciplineIds: null },
      })
      .pipe(
        tap((students) => {
          ctx.patchState({ smartMatchStudents: students });
          const currentStudentId = ctx.getState().smartMatchStudentId;
          if (!currentStudentId && students?.length) {
            ctx.patchState({
              smartMatchStudentId: students[0].id,
            });
          }
        }),
        finalize(() =>
          ctx.patchState({
            isLoading: this.isListLoading(ctx),
          }),
        ),
      );
  }

  @Action(LoadSmartMatchSchools)
  loadSmartMatchSchools(ctx: StateContext<PlacementsDatabaseStateModel>) {
    ctx.patchState({ isLoading: true });

    return this.schoolService.getSchoolsAvailableForMatching().pipe(
      tap((response) => {
        ctx.patchState({ smartMatchSchools: response.schools });
        const currentSchoolId = ctx.getState().smartMatchSchoolId;
        if (!currentSchoolId && response.schools?.length) {
          ctx.patchState({
            smartMatchSchoolId: response.schools[0].id,
          });
        }
      }),
      finalize(() =>
        ctx.patchState({
          isLoading: this.isListLoading(ctx),
        }),
      ),
    );
  }

  @Action(SetPlacementsDatabaseFiltersTab)
  setPlacementsDatabaseFiltersTab(
    ctx: StateContext<PlacementsDatabaseStateModel>,
    action: SetPlacementsDatabaseFiltersTab,
  ) {
    const { pagination } = ctx.getState();

    ctx.patchState({
      pagination: { ...pagination, currentPage: 1 },
      isLoading: true,
      filtersTab: action.payload.tab,
      previewOpportunityId: null,
      previewOrganizationId: null,
    });
  }

  @Action(CloseStudentNotificationTooltip)
  closeStudentNotificationTooltip(
    ctx: StateContext<PlacementsDatabaseStateModel>,
  ) {
    const { smartMatchMessageDismissedForStudents, smartMatchStudentId } =
      ctx.getState();

    ctx.patchState({
      smartMatchMessageDismissedForStudents: {
        ...smartMatchMessageDismissedForStudents,
        [smartMatchStudentId!]: true,
      },
    });
  }

  @Action(CloseSchoolNotificationTooltip)
  closeSchoolNotificationTooltip(
    ctx: StateContext<PlacementsDatabaseStateModel>,
  ) {
    const { smartMatchMessageDismissedForSchools, smartMatchSchoolId } =
      ctx.getState();

    ctx.patchState({
      smartMatchMessageDismissedForSchools: {
        ...smartMatchMessageDismissedForSchools,
        [smartMatchSchoolId!]: true,
      },
    });
  }

  @Action(InitializePlacementsDatabaseFilters)
  initializeFilters(
    ctx: StateContext<PlacementsDatabaseStateModel>,
    action: InitializePlacementsDatabaseFilters,
  ) {
    const { filters = ctx.getState().manualFilters } = action.payload;

    ctx.patchState({
      manualFilters: filters,
      filtersInitialized: true,
    });
  }

  private loadListItems(
    ctx: StateContext<PlacementsDatabaseStateModel>,
    applyViewPort?: boolean,
  ) {
    const { listType } = ctx.getState();

    if (listType === PlacementsDatabaseListType.Opportunities) {
      ctx.patchState({
        organizations: null,
        previewOrganizationId: null,
        selectedTab: null,
      });
      return this.loadOpportunities(ctx, applyViewPort);
    } else {
      ctx.patchState({
        opportunities: null,
        previewOpportunityId: null,
      });
      return this.loadOrganizations(ctx, applyViewPort);
    }
  }

  private loadOrganizations(
    ctx: StateContext<PlacementsDatabaseStateModel>,
    applyViewportFilter: boolean = true,
  ) {
    this.cancelPrevious$.next();

    const { filtersTab, smartMatchStudentId, smartMatchSchoolId } =
      ctx.getState();
    const smartMatchActive =
      filtersTab === PlacementsDatabaseFilterTab.StudentSmartMatch ||
      filtersTab === PlacementsDatabaseFilterTab.SchoolSmartMatch;

    if (smartMatchActive && !(smartMatchStudentId || smartMatchSchoolId)) {
      ctx.patchState({ organizations: [], itemsCount: 0 });
      return EMPTY;
    }

    ctx.patchState({
      isLoading: true,
    });

    return this.getSearchOrganizationsRequest(
      filtersTab!,
      applyViewportFilter,
      ctx,
    ).pipe(
      tap((response) =>
        ctx.patchState({
          organizations: response.organizations,
          itemsCount: response.showingCount,
        }),
      ),
      takeUntil(this.cancelPrevious$),
      finalize(() =>
        ctx.patchState({
          isLoading: false,
        }),
      ),
    );
  }

  private getSearchOrganizationsRequest(
    filterTab: PlacementsDatabaseFilterTab,
    applyViewport: boolean,
    ctx: StateContext<PlacementsDatabaseStateModel>,
  ): Observable<{ showingCount: number; organizations: Organization[] }> {
    switch (filterTab) {
      case PlacementsDatabaseFilterTab.StudentSmartMatch:
        return this.organizationsDatabaseService.smartMatchClinicsToStudent({
          body: this.getSmartMatchClinicsToStudentBody(ctx, applyViewport),
        });
      case PlacementsDatabaseFilterTab.SchoolSmartMatch:
        return this.organizationsDatabaseService
          .smartMatchClinicsToSchool({
            body: this.getSmartMatchClinicsToSchoolBody(ctx, applyViewport),
          })
          .pipe(
            map((response) => ({
              showingCount: response.showingCount,
              organizations: response.clinics,
            })),
          );
      case PlacementsDatabaseFilterTab.Filters:
        return this.organizationsDatabaseService.searchOrganizations({
          body: this.getSearchOrganizationsBody(ctx, applyViewport),
        });
    }
  }

  private getSearchOrganizationsBody(
    ctx: StateContext<PlacementsDatabaseStateModel>,
    applyViewportFilter: boolean,
  ) {
    const { pagination, viewport, manualFilters, locationFilters } =
      ctx.getState();

    return {
      pagination: {
        page: pagination.currentPage,
        itemsPerPage: pagination.itemsPerPage,
      } satisfies Pagination,
      filters: {
        ...manualFilters,
        locationFilter: locationFilters?.location,
        viewPort: applyViewportFilter ? viewport : null,
      },
    } satisfies SearchOrganizationsRequest;
  }

  private getSmartMatchClinicsToStudentBody(
    ctx: StateContext<PlacementsDatabaseStateModel>,
    applyViewportFilter: boolean,
  ) {
    const {
      pagination,
      viewport: viewPort,
      manualFilters,
      smartMatchStudentId,
    } = ctx.getState();

    return {
      studentId: smartMatchStudentId!,
      viewPort: applyViewportFilter ? viewPort : null,
      searchText: manualFilters.searchText || null,
      pagination: {
        page: pagination.currentPage,
        itemsPerPage: pagination.itemsPerPage,
      } satisfies Pagination,
    } satisfies SmartMatchClinicsToStudentRequest;
  }

  private getSmartMatchClinicsToSchoolBody(
    ctx: StateContext<PlacementsDatabaseStateModel>,
    applyViewportFilter: boolean,
  ) {
    const {
      pagination,
      viewport: viewPort,
      manualFilters,
      smartMatchSchoolId,
    } = ctx.getState();

    return {
      schoolId: smartMatchSchoolId!,
      viewPort: applyViewportFilter ? viewPort : null,
      searchText: manualFilters.searchText || null,
      pagination: {
        page: pagination.currentPage,
        itemsPerPage: pagination.itemsPerPage,
      } satisfies Pagination,
    } satisfies SmartMatchClinicsToSchoolRequest;
  }

  private loadOpportunities(
    ctx: StateContext<PlacementsDatabaseStateModel>,
    applyViewportFilter: boolean = true,
  ) {
    this.cancelPrevious$.next();

    const { filtersTab, smartMatchStudentId } = ctx.getState();
    const smartMatchActive =
      filtersTab === PlacementsDatabaseFilterTab.StudentSmartMatch;

    if (smartMatchActive && !smartMatchStudentId) {
      ctx.patchState({ opportunities: [], itemsCount: 0 });
      return EMPTY;
    }

    ctx.patchState({
      isLoading: true,
    });

    const body = smartMatchActive
      ? this.getSmartMatchOpportunitiesBody(ctx, applyViewportFilter)
      : this.getSearchOpportunitiesBody(ctx, applyViewportFilter);

    return iif(
      () => smartMatchActive,
      this.opportunitiesService.opportunitiesPublishedSmartMatchPost({
        body: body as SmartMatchOpportunitiesRequest,
      }),
      this.opportunitiesService.opportunitiesPublishedSearchPost({
        body: body as SearchPublishedOpportunitiesRequest,
      }),
    ).pipe(
      tap((response) =>
        ctx.patchState({
          opportunities: response.opportunities,
          itemsCount: response.showingCount,
        }),
      ),
      takeUntil(this.cancelPrevious$),
      finalize(() =>
        ctx.patchState({
          isLoading: false,
        }),
      ),
    );
  }

  private getSearchOpportunitiesBody(
    ctx: StateContext<PlacementsDatabaseStateModel>,
    applyViewportFilter: boolean,
  ) {
    const {
      pagination,
      viewport: viewPort,
      manualFilters,
      locationFilters,
    } = ctx.getState();

    return {
      clinicId: null,
      pagination: {
        page: pagination.currentPage,
        itemsPerPage: pagination.itemsPerPage,
      } satisfies Pagination,
      filters: {
        ...manualFilters,
        locationFilter: locationFilters?.location,
        viewPort: applyViewportFilter ? viewPort : null,
        startDate: DateUtility.convertToDateOnlyString(
          manualFilters.startDate || DateUtility.getFirstDayOfMonth(new Date()),
        ),
        endDate: DateUtility.convertToDateOnlyString(
          manualFilters.endDate || DateUtility.getFirstDayOfMonth(new Date()),
        ),
      },
    } satisfies SearchPublishedOpportunitiesRequest;
  }

  private getSmartMatchOpportunitiesBody(
    ctx: StateContext<PlacementsDatabaseStateModel>,
    applyViewportFilter: boolean,
  ) {
    const {
      pagination,
      viewport: viewPort,
      manualFilters,
      smartMatchStudentId,
    } = ctx.getState();

    return {
      studentId: smartMatchStudentId!,
      viewPort: applyViewportFilter ? viewPort : null,
      searchText: manualFilters.searchText || null,
      pagination: {
        page: pagination.currentPage,
        itemsPerPage: pagination.itemsPerPage,
      } satisfies Pagination,
    } satisfies SmartMatchOpportunitiesRequest;
  }

  private isListLoading(ctx: StateContext<PlacementsDatabaseStateModel>) {
    const { organizations, opportunities, listType } = ctx.getState();
    return (
      listType === null ||
      (listType === PlacementsDatabaseListType.Organizations
        ? organizations === null
        : opportunities === null)
    );
  }
}
